
import LoadingButton, { LoadingButtonProps } from "@mui/lab/LoadingButton";
import { useEffect, useRef } from "react";
import classes from './bt-button.module.scss';
interface BTButtonProps {
  children: React.ReactNode;
  sx?: any;
  iconsrcbefore?: string;
  iconsrcafter?: string;
  iconW?: number;
  iconH?: number;
  onClick?: (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  btvariant?: 'primary' | 'secondary' | 'text' | 'pink' | 'upgrade' | 'admin-api';
  translate?: string;
}

function BTButton(props: BTButtonProps & LoadingButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  
  // Protect against Google Translate DOM conflicts
  useEffect(() => {
    const button = buttonRef.current;
    if (!button) return;

    // Add protection against DOM manipulation errors
    const originalInsertBefore = Node.prototype.insertBefore;
    const originalRemoveChild = Node.prototype.removeChild;

    const safeInsertBefore = function(this: Node, newNode: Node, referenceNode: Node | null) {
      try {
        // Check if referenceNode is actually a child of this node
        if (referenceNode && !this.contains(referenceNode)) {
          console.warn('Google Translate DOM conflict prevented: referenceNode is not a child');
          return newNode; // Return the newNode to satisfy the return type
        }
        return originalInsertBefore.call(this, newNode, referenceNode);
      } catch (error) {
        console.warn('DOM insertBefore error caught and handled:', error);
        // Fallback: append to end if insertBefore fails
        return this.appendChild(newNode);
      }
    };

    const safeRemoveChild = function(this: Node, child: Node) {
      try {
        if (!this.contains(child)) {
          console.warn('Google Translate DOM conflict prevented: child is not in parent');
          return child; // Return the child to satisfy the return type
        }
        return originalRemoveChild.call(this, child);
      } catch (error) {
        console.warn('DOM removeChild error caught and handled:', error);
        return child;
      }
    };

    // Apply safe methods to the button and its descendants
    const applyProtection = (element: Element) => {
      (element as any).insertBefore = safeInsertBefore;
      (element as any).removeChild = safeRemoveChild;
      
      // Apply to all children
      Array.from(element.children).forEach(applyProtection);
    };

    applyProtection(button);

    // Cleanup
    return () => {
      if (button) {
        (button as any).insertBefore = originalInsertBefore;
        (button as any).removeChild = originalRemoveChild;
      }
    };
  }, [props.loading]);

  let buttonTheme;
  switch (props.btvariant) {
    case 'secondary': {
      buttonTheme = {
        cursor: "pointer",
        margin: "0",
        textDecoration: "none",
        borderRadius: "8px",
        backgroundColor: "var(--billing-btn-bg)",
        color: "var(--link-text-color)",
        fontSize: "12px",
        fontWeight: "600",
        lineHeight: "16px",
        padding: "6px",
        textAlign: "center",
        textTransform: "capitalize",
        maxWidth: "80%",
        fontFamily: "'Poppins', 'Inter', sans-serif",
      };
      break;
    }
    case 'text': {
      buttonTheme = {
        cursor: "pointer",
        margin: "0",
        textDecoration: "none",
        borderRadius: "8px",
        color: "var(--link-text-color)",
        fontSize: "12px",
        fontWeight: "600",
        lineHeight: "16px",
        padding: "6px",
        textAlign: "center",
        textTransform: "capitalize",
        maxWidth: "80%",
        background: 'transparent',
        fontFamily: "'Poppins', 'Inter', sans-serif",
      };
      
      break;
    }
    case 'pink': {
      buttonTheme = {
        cursor: "pointer",
        margin: "0",
        textDecoration: "none",
        borderRadius: "8px",
        color: "#ffffff",
        fontSize: "14px",
        padding: "6px",
        textAlign: "center",
        textTransform: "capitalize",
        fontFamily: "'Poppins', 'Inter', sans-serif",
        fontWeight: "800",
        // Historically pink; Blueticks blue now (same token as the floating action button).
        background: "var(--fab-bg)",
        ...props.sx,
      };
      
      break;
    }
         case 'upgrade': {
       buttonTheme = {
         cursor: "pointer",
         margin: "0",
         textDecoration: "none",
         borderRadius: "8px",
         color: "var(--upgrade-button-color) !important",
         fontSize: "12px",
         fontWeight: "600",
         lineHeight: "16px",
         padding: "6px",
         textAlign: "center",
         textTransform: "capitalize",
         maxWidth: "80%",
         background: 'transparent',
         fontFamily: "'Poppins', 'Inter', sans-serif",
         transition: 'color 0.2s ease-in-out',
         '&:hover': {
           color: 'var(--upgrade-button-hover) !important',
         },
         '&.MuiButton-root': {
           color: 'var(--upgrade-button-color) !important',
           '&:hover': {
             color: 'var(--upgrade-button-hover) !important',
           },
         },
         '&.MuiButtonBase-root': {
           color: 'var(--upgrade-button-color) !important',
           '&:hover': {
             color: 'var(--upgrade-button-hover) !important',
           },
         },
         ...props.sx,
       };
       
       break;
     }
     case 'admin-api': {
       buttonTheme = {
         "&:disabled": {
           opacity: '0.8',
           color: "var(--color-primary-contrast)"
         },
         "& .MuiButton-startIcon": {
           display: props.loading && "none",
         },
         "& .MuiLoadingButton-loadingIndicator": {
           position: "inherit",
           left: "unset",
           transform: "none",
           marginInlineEnd: "8px",
           color: "var(--color-primary-contrast)"
         },
         transition: '0.2s',
         // --fab-text is the label paired with --fab-bg in each theme: white on
         // the (deepened) light gradient, >=4.62:1; deep ink on the dark one,
         // 6.61:1. Not --color-always-white, which would be 2.14:1 in dark.
         color: "var(--fab-text) !important",
         fontWeight: "600",
         lineHeight: "21px",
         borderRadius: "8px",
         padding: "7.5px 28px",
         background: "var(--fab-bg)",
         whiteSpace: 'nowrap',
         textTransform: 'capitalize',
         fontFamily: "'Poppins', 'Inter', sans-serif",
         "&.MuiButton-root": {
           color: "var(--fab-text) !important",
         },
         "&.MuiButtonBase-root": {
           color: "var(--fab-text) !important",
         },
         ...props.sx,
       };
       break;
     }
         case 'primary':
     default: {
       buttonTheme = {
         "&:disabled": {
           opacity: '0.8',
           color: "var(--color-primary-contrast)"
         },
         "& .MuiButton-startIcon": {
           display: props.loading && "none",
         },
         "& .MuiLoadingButton-loadingIndicator": {
           // left: props.startIcon ? '30%' : "8%",
           position: "inherit",
           left: "unset",
           transform: "none",
           marginInlineEnd: "8px",
           color: "var(--color-primary-contrast)"
         },
         transition: '0.2s',
         color: "var(--fab-text)",
         fontWeight: "600",
         lineHeight: "21px",
         borderRadius: "8px",
         padding: "7.5px 28px",
         background: "var(--fab-bg)",
         whiteSpace: 'nowrap',
         textTransform: 'capitalize',
         fontFamily: "'Poppins', 'Inter', sans-serif",
         ...props.sx,
       };
       break;
     }
  }

  return (
    <LoadingButton
      {...props}
      ref={buttonRef}
      className={classes.btButton}
      loadingPosition={props.startIcon ? 'start' : undefined}
      sx={buttonTheme}
      translate={props.translate}
    >
      {!props.loading && props.iconsrcbefore && (
        <img src={props.iconsrcbefore}
          alt=""
          style={{
            marginRight: "5px",
            width: props.iconW,
            height: props.iconH,
          }} />

      )}

      {props.children}

      {!props.loading && props.iconsrcafter &&
        <img src={props.iconsrcafter} alt="" style={{ marginLeft: "5px" }}></img>
      }
    </LoadingButton >
  );
}
export default BTButton;
